import type { PropsWithChildren } from 'react';

import { type AxiosError, HttpStatusCode } from 'axios';
import { Navigate } from 'react-router';
import { toast } from 'react-toastify';

import SuspenseLoader from '@ll-platform/frontend/components/SuspenseLoader';
import { ActivityType } from '@ll-platform/frontend/core/analytics/events';
import { useActivityTracker } from '@ll-platform/frontend/core/analytics/useActivityTracker';
import { useActiveUser } from '@ll-platform/frontend/core/auth/hooks/useActiveUser';
import { isUserInternal } from '@ll-platform/frontend/core/auth/utils/isInternal';
import { Pages } from '@ll-platform/frontend/core/router/pages';
import { useGetProjectUsers } from '@ll-platform/frontend/features/projects/async/useProjectsQueries';
import { useActiveProjectId } from '@ll-platform/frontend/features/projectWizard/hooks/useActiveProject';

const toastId = 'unauthorizedAccessToProject';

export const ActiveProjectMemberGuard = ({ children }: PropsWithChildren) => {
  const { activeUser } = useActiveUser();
  const projectId = useActiveProjectId();
  const { trackEvent } = useActivityTracker();

  // Backend throws 403 if external user is not a member of the project
  const {
    data: projectUsers,
    isPending,
    error,
  } = useGetProjectUsers(
    {
      projectId,
    },
    {
      enabled: !isUserInternal(activeUser),
      retry: 1,
    },
  );

  if (isUserInternal(activeUser)) {
    return children;
  }

  if (isPending) {
    return <SuspenseLoader />;
  }

  if (
    !projectUsers &&
    error &&
    (error as AxiosError).status === HttpStatusCode.Forbidden
  ) {
    trackEvent(ActivityType.ProjectGuardAccessDenied, {
      projectId,
      userId: activeUser._id,
      email: activeUser.email,
    });
    toast.info(
      `Page you tried to open is accessible to project members only. Please contact your project's point of contact if needed.`,
      {
        toastId,
      },
    );
    return <Navigate to={Pages.ExternalProjects} />;
  }

  return children;
};
